import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://aaktradex.in';

const C = {
  green: '#3B6D11', lgreen: '#EAF3DE', bgreen: '#C0DD97',
  amber: '#854F0B', lamber: '#FAEEDA', bamber: '#FAC775',
  red:   '#A32D2D', lred:   '#FCEBEB', bred:   '#F7C1C1',
  blue:  '#185FA5', lblue:  '#E6F1FB', bblue:  '#B5D4F4',
  purple:'#6B21A8', lpurple:'#F3E8FF', bpurple:'#C4B5FD',
  gray:  '#6B7280', lgray:  '#F3F4F6', bgray:  '#D1D5DB',
};

function SignalBadge({ signal }) {
  if (!signal || signal === "N/A") {
    return <span style={{ fontSize: 10, color: 'var(--t3)' }}>—</span>;
  }
  const styles = {
    HOLD:            { bg: C.lgreen, color: C.green },
    STRONG_EXIT:     { bg: C.red,    color: '#fff' },
    EXIT:            { bg: C.amber,  color: '#fff' },
    DEEP_LOSS_WATCH: { bg: C.purple, color: '#fff' },
    UNKNOWN:         { bg: C.lgray,  color: C.gray },
  }[signal] || { bg: C.lgray, color: C.gray };

  const label = signal.replace(/_/g, ' ');
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 4,
      background: styles.bg, color: styles.color, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

function fmtRs(n) {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}Rs${Math.round(n).toLocaleString('en-IN')}`;
}

function fmtPct(n) {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function fmtLakh(n) {
  if (n == null) return '—';
  return `Rs${(n / 100000).toFixed(2)}L`;
}

export default function PLPortfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllRows, setShowAllRows] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pl/portfolio`);
      setData(res.data);
    } catch (e) {
      console.error("PL portfolio fetch:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, [fetchData]);

  if (loading) return <div style={{ padding: 12, fontSize: 11 }}>Loading PL portfolio...</div>;
  if (!data || !data.active_cycle) {
    return (
      <div style={{
        padding: 16, background: 'var(--bg1)', borderRadius: 10,
        border: '1px solid var(--border2)', marginBottom: 16, fontSize: 11,
      }}>
        No active PL cycle. Set up a Trailblazers watchlist to begin tracking.
      </div>
    );
  }

  const held = (data.holdings || []).filter(h => h.held_qty > 0);
  const exited = (data.holdings || []).filter(h => h.held_qty === 0);
  const rowsToShow = showAllRows ? [...held, ...exited] : held;

  return (
    <div style={{
      background: 'var(--bg1)', border: '1px solid var(--border2)',
      borderRadius: 10, padding: 16, marginBottom: 16,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 12,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.purple }}>
            PL Portfolio — Real Money
          </div>
          <div style={{ fontSize: 10, color: 'var(--t2)', marginTop: 2 }}>
            Cycle {data.cycle_start_date} to {data.cycle_end_date} · {data.days_to_rebalance} days to rebalance
          </div>
        </div>
        <button
          onClick={() => setShowAllRows(v => !v)}
          style={{
            fontSize: 10, padding: '4px 10px', borderRadius: 6,
            background: C.lblue, color: C.blue, border: `0.5px solid ${C.bblue}`,
            cursor: 'pointer', fontWeight: 500,
          }}
        >
          {showAllRows ? 'Held only' : 'Show all (incl. exited)'}
        </button>
      </div>

      {/* Action banner */}
      {(() => {
        const signals = (data.holdings || [])
          .filter(h => h.held_qty > 0)
          .map(h => h.signal_info?.signal)
          .filter(s => s && s !== 'HOLD' && s !== 'N/A' && s !== 'UNKNOWN');
        if (signals.length === 0) {
          return (
            <div style={{
              padding: 10, marginBottom: 10, borderRadius: 6,
              background: C.lgreen, color: C.green,
              fontSize: 11, fontWeight: 600, border: `1px solid ${C.bgreen}`,
            }}>
              ✓ HOLD ALL — no exit triggers fired. Continue holding until next signal or July 13 rebalance.
            </div>
          );
        }
        return (
          <div style={{
            padding: 10, marginBottom: 10, borderRadius: 6,
            background: C.lamber, color: C.amber,
            fontSize: 11, fontWeight: 600, border: `1px solid ${C.bamber}`,
          }}>
            ⚠ {signals.length} position(s) have exit signals — see table below
          </div>
        );
      })()}

      {/* Summary cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: 8, marginBottom: 12,
      }}>
        {[
          ['REALIZED', fmtRs(data.summary?.total_realized_pnl),
           data.summary?.total_realized_pnl >= 0 ? C.green : C.red],
          ['UNREALIZED', fmtRs(data.summary?.total_unrealized_pnl),
           data.summary?.total_unrealized_pnl >= 0 ? C.green : C.red],
          ['CYCLE NET', fmtRs(data.summary?.cycle_net_pnl),
           data.summary?.cycle_net_pnl >= 0 ? C.green : C.red],
          ['INVESTED (held)', fmtLakh(data.summary?.total_currently_invested), 'var(--t1)'],
          ['CURRENT VALUE', fmtLakh(data.summary?.total_current_value), 'var(--t1)'],
          ['HELD', `${data.summary?.held_tickers_count} tickers`, 'var(--t1)'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ padding: 8, background: 'var(--bg2)', borderRadius: 6 }}>
            <div style={{ fontSize: 9, color: 'var(--t2)' }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Holdings table */}
      {rowsToShow.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--t2)', padding: 10 }}>
          Nothing held right now. All positions exited.
        </div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
                <th style={{ textAlign: 'left', padding: 6 }}>Ticker</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Held Qty</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Avg Cost</th>
                <th style={{ textAlign: 'right', padding: 6 }}>LTP</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Value</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Unrealized</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Realized</th>
                <th style={{ textAlign: 'center', padding: 6 }}>Signal</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rowsToShow.map(h => {
                const sig = h.signal_info || {};
                const ur = h.unrealized_gross;
                const urPct = h.unrealized_pct;
                const isHeld = h.held_qty > 0;
                return (
                  <tr key={h.ticker} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                    <td style={{ padding: 6, fontWeight: 600 }}>
                      {h.ticker}
                      {!isHeld && <span style={{ fontSize: 9, color: 'var(--t3)', marginLeft: 6 }}>(exited)</span>}
                    </td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{isHeld ? h.held_qty : '—'}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{h.avg_cost?.toFixed(2)}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>
                      {h.ltp ? h.ltp.toFixed(2) : '—'}
                    </td>
                    <td style={{ padding: 6, textAlign: 'right' }}>
                      {h.current_value ? fmtRs(h.current_value).replace('+', '') : '—'}
                    </td>
                    <td style={{ padding: 6, textAlign: 'right', fontWeight: 600,
                      color: !isHeld ? 'var(--t3)' :
                             (ur || 0) >= 0 ? C.green : C.red,
                    }}>
                      {isHeld && ur != null ? `${fmtRs(ur)} (${fmtPct(urPct)})` : '—'}
                    </td>
                    <td style={{ padding: 6, textAlign: 'right', fontWeight: 600,
                      color: (h.realized_pnl || 0) >= 0 ? C.green : C.red,
                    }}>
                      {h.realized_pnl !== 0 ? fmtRs(h.realized_pnl) : '—'}
                    </td>
                    <td style={{ padding: 6, textAlign: 'center' }}>
                      <SignalBadge signal={sig.signal} />
                    </td>
                    <td style={{ padding: 6, fontSize: 10, color: 'var(--t3)' }}>
                      {sig.reasons?.length
                        ? sig.reasons.join(' · ')
                        : isHeld ? (
                            urPct > 3 ? 'In profit — watch for MACD/rebalance triggers'
                            : urPct > 0 ? 'Marginally profitable — needs +3% for exit triggers'
                            : urPct > -5 ? 'Modest loss — hold to rebalance'
                            : 'Loss watch — if EMA cracks + MACD negative, DEEP_LOSS fires'
                          ) : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
